import type { ContentBlockStatus } from "@/api/chat/chat.types";
import { cn } from "@/lib/utils";
import { CheckIcon, LoaderIcon, ShieldCheck, XCircleIcon } from "lucide-react";
import type { FC } from "react";

interface ProcessingPartProps {
    name: string;
    status?: ContentBlockStatus;
    summary_text?: string;
}

const STATUS_CONFIG: Record<
    ContentBlockStatus,
    { icon: FC<{ className?: string }>; color: string; label: string }
> = {
    in_progress: {
        icon: ({ className }) => (
            <LoaderIcon className={cn("animate-spin", className)} />
        ),
        color: "text-muted-foreground",
        label: "Running",
    },
    complete: {
        icon: CheckIcon,
        color: "text-emerald-600 dark:text-emerald-400",
        label: "Complete",
    },
    error: {
        icon: XCircleIcon,
        color: "text-destructive",
        label: "Error",
    },
};

const TOOL_ICONS: Record<string, FC<{ className?: string }>> = {
    guard_validation: ShieldCheck,
    content_filter: ShieldCheck,
    api_error: XCircleIcon,
};

export const ProcessingPart: FC<ProcessingPartProps> = ({
    name,
    status = "in_progress",
    summary_text,
}) => {
    const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.in_progress;
    const ToolIcon = TOOL_ICONS[name] ?? ShieldCheck;
    const StatusIcon = config.icon;

    return (
        <div
            className={cn(
                "flex items-start gap-3 rounded-lg border px-4 py-3 text-sm",
                "fade-in slide-in-from-bottom-1 animate-in duration-150",
                status === "error" && "border-destructive/30 bg-destructive/5",
                status === "complete" &&
                    "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900/30 dark:bg-emerald-950/20",
                status === "in_progress" && "border-border bg-muted/30"
            )}
        >
            <ToolIcon className="text-muted-foreground mt-0.5 size-4 shrink-0" />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex items-center gap-2">
                    <span className="text-foreground font-medium">
                        {formatName(name)}
                    </span>
                    <StatusIcon
                        className={cn("size-3.5 shrink-0", config.color)}
                    />
                </div>
                {summary_text && (
                    <p
                        className={cn(
                            "text-muted-foreground text-xs leading-relaxed",
                            status === "error" && "text-destructive"
                        )}
                    >
                        {summary_text}
                    </p>
                )}
            </div>
        </div>
    );
};

function formatName(name: string): string {
    return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
